import {
  lazy,
  Suspense,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { ClientOnly } from "@tanstack/react-router";
import { createClientOnlyFn } from "@tanstack/react-start";
import { SurfaceProvider } from "~/lib/surface-context";
import { useAppAuth } from "~/lib/auth/app-auth";
import {
  getSimpleToasts,
  subscribeSimpleToasts,
} from "~/lib/toast/simple-toast-store";
const ToastProvider = lazy(
  createClientOnlyFn(async () => {
    const { ToastProvider } = await import("~/lib/toast/provider");
    return { default: ToastProvider };
  }),
);
import { m } from "~/lib/i18n";

const hasSimpleToasts = () => getSimpleToasts().length > 0;
const noServerToasts = () => false;

function OnDemandToastProvider() {
  const { authenticated } = useAppAuth();
  const hasPendingToast = useSyncExternalStore(
    subscribeSimpleToasts,
    hasSimpleToasts,
    noServerToasts,
  );
  const [activated, setActivated] = useState(false);
  const shouldActivate = authenticated || hasPendingToast;
  // Keep the renderer mounted after its first use so dismissal animations and
  // execution tracking survive an empty stack or an auth transition. Simple
  // toasts remain in their existing store while the lazy module is loading.
  if (shouldActivate && !activated) {
    setActivated(true);
  }
  return activated || shouldActivate ? (
    <Suspense fallback={null}>
      <ToastProvider />
    </Suspense>
  ) : null;
}

export function MissingPrivyConfig() {
  return (
    <div className="mx-auto mt-8 w-full max-w-2xl rounded-[1.75rem] border border-red-300/70 bg-red-50/90 p-5 text-status-negative-foreground shadow-sm">
      <h1 className="text-body font-semibold">
        {m.components_app_providers_missing_privy_heading({
          environmentVariable: "`VITE_PRIVY_APP_ID`",
        })}
      </h1>
      <p className="mt-2 text-meta">
        {m.components_app_providers_missing_privy_description({
          appName: "`webapp-v2`",
          environmentVariable: "`VITE_PRIVY_APP_ID`",
        })}
      </p>
    </div>
  );
}

function GlobalUiProviders({ children }: { children: ReactNode }) {
  // ToastProvider renders the bottom-center pill stack (execution + simple
  // toasts — v1 parity) and the expanded detail modal.
  return (
    <>
      <SurfaceProvider value={1}>{children}</SurfaceProvider>
      <ClientOnly>
        <OnDemandToastProvider />
      </ClientOnly>
    </>
  );
}

export function AppUiProviders({ children }: { children: ReactNode }) {
  return <GlobalUiProviders>{children}</GlobalUiProviders>;
}
